import { createSlice } from '@reduxjs/toolkit';
import { getDefaultRange } from '../dashboard/dashboardUtils';
import { ATTENTION_REASON } from './attentionConstants';
import { fetchAttention } from './attentionThunks';
import { fetchAvailableCouriers } from './attentionActionThunks';

/**
 * The attention queue.
 *
 * Filters are not persisted, for the same reason the dashboard's are not: the
 * range is relative to today, and a rehydrated one opens the queue over a dead
 * week. It also means arriving from the dashboard always sets the filters
 * fresh, instead of fighting whatever was stored.
 */
const initialState = {
  data: [],
  total: 0,
  isLoading: false,
  error: null,
  /** Cached for the session: it is a catalog, not a per-shipment lookup. */
  couriers: [],
  couriersLoaded: false,
  filters: {
    ...getDefaultRange(),
    company: 'all',
    // Only the staff sets it: the queue is then the whole platform's, and this
    // is the first rung of the ladder — everything → one operator → one of its
    // companies. An admin never leaves it on the sentinel.
    admin_id: 'all',
    reason: ATTENTION_REASON.ALL,
    page: 1,
    rowsPerPage: 20,
  },
};

const attentionSlice = createSlice({
  name: 'attention',
  initialState,
  reducers: {
    /** Any filter change but paging sends you back to the first page. */
    setFilters: (state, action) => {
      const patch = action.payload;
      state.filters = {
        ...state.filters,
        ...patch,
        page: patch.page ?? 1,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAttention.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAttention.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.results || [];
        state.total = action.payload.count || 0;
      })
      .addCase(fetchAttention.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.data = [];
      })
      .addCase(fetchAvailableCouriers.fulfilled, (state, action) => {
        state.couriers = action.payload;
        state.couriersLoaded = true;
      });
  },
});

export const { setFilters } = attentionSlice.actions;

export default attentionSlice.reducer;
